import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Percent, TrendingUp, DollarSign, Wallet, Calculator, ArrowDownToLine, ArrowUpFromLine,
  ArrowLeftRight, ArrowRight, AlertTriangle
} from 'lucide-react';
import { FaCheckCircle, FaTimesCircle } from 'react-icons/fa';
import { useTheme } from '../../contexts/ThemeContext';
import { useDevMode } from '../../hooks/useDevMode';
import DevModePanel from '../dev/DevModePanel';
import ConnectJobTreadHero from '../portal/ConnectJobTreadHero';
import MetricWidget from './MetricWidget';
import { statusStyle } from './financeBenchmarks';
import { saveQuickOverhead } from '../../services/overheadService';
import {
  useFinanceAnalytics,
  FINANCE_TIMEFRAMES
} from '../../hooks/useFinanceAnalytics';
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from 'recharts';

const OVERHEAD_CALCULATOR_PATH = '/portal/finance/overhead-calculator';

const fmtPctAxis = (v) => `${Math.round(v)}%`;
const fmtDollar = (v) => (v == null ? '—' : `$${Math.round(v).toLocaleString()}`);

const JT_HERO_DESCRIPTION =
  'Your gross profit, margins, receivables, and payables all flow straight from your JobTread account — live, with nothing to enter by hand.';

const ConnectionPill = ({ connected, label }) => (
  <span
    className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${
      connected
        ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-300'
        : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
    }`}
  >
    {connected ? <FaCheckCircle className="text-xs" /> : <FaTimesCircle className="text-xs" />}
    {label}
  </span>
);

// Overhead card with an inline quick-adjust: type a monthly or annual fixed
// total and it writes straight to the Overhead Calculator (quick mode) and
// re-runs the dashboard math. Full itemization still lives on the calculator.
const OverheadQuickCard = ({ overhead, loading, devMode, onSaved, saveUserId = null }) => {
  const hasData = overhead.source !== 'none';
  const [freq, setFreq] = useState('Monthly');
  const [input, setInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  // Prefill from the current overhead whenever it (or the chosen frequency) changes.
  useEffect(() => {
    if (overhead.monthly == null) { setInput(''); return; }
    const v = freq === 'Annual' ? overhead.annual : overhead.monthly;
    setInput(String(Math.round(v)));
  }, [overhead.monthly, overhead.annual, freq]);

  const handleSave = async () => {
    const amount = parseFloat(input);
    if (!Number.isFinite(amount) || amount < 0 || saving) return;
    setSaving(true);
    try {
      await saveQuickOverhead(amount, freq, saveUserId);
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2000);
      onSaved?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <MetricWidget
      title="Overhead"
      titleMeta={hasData && overhead.mode === 'quick' ? 'Quick total' : undefined}
      icon={<Calculator className="w-4 h-4" />}
      value={hasData ? overhead.value : null}
      source={hasData ? 'manual' : 'none'}
      sub={hasData ? overhead.sub : null}
      loading={loading}
      trace={devMode ? overhead.trace : null}
    >
      <div className="space-y-3">
        {!hasData && (
          <p className="text-sm text-gray-500 dark:text-gray-400">
            Enter your overhead below to unlock net margin.
          </p>
        )}
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Quick adjust</span>
            <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden text-xs">
              {['Monthly', 'Annual'].map((f) => (
                <button
                  key={f}
                  type="button"
                  onClick={() => setFreq(f)}
                  className={`px-2.5 py-1 font-medium transition-colors ${
                    freq === f
                      ? 'bg-red-600 text-white'
                      : 'text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm">$</span>
              <input
                type="number"
                min="0"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSave(); }}
                placeholder={freq === 'Annual' ? 'Annual fixed overhead' : 'Monthly fixed overhead'}
                className="w-full pl-6 pr-2 py-1.5 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-red-500"
              />
            </div>
            <button
              type="button"
              onClick={handleSave}
              disabled={saving}
              className="px-3 py-1.5 rounded-lg bg-red-600 hover:bg-red-700 disabled:opacity-60 text-white text-sm font-semibold shrink-0"
            >
              {saving ? 'Saving…' : savedFlash ? 'Saved ✓' : 'Save'}
            </button>
          </div>
          <p className="text-[11px] text-gray-400 mt-1">
            Saves as one fixed total and updates your Overhead Calculator.
          </p>
        </div>
        <div className="flex items-center justify-between gap-3 pt-1">
          <Link
            to={OVERHEAD_CALCULATOR_PATH}
            className="inline-flex items-center gap-1 text-sm font-medium text-red-600 hover:text-red-700 hover:underline shrink-0"
          >
            Open calculator
            <ArrowRight className="w-3.5 h-3.5" />
          </Link>
          <span className="text-xs text-gray-500 dark:text-gray-400 text-right">
            Feeds the net margin math above
          </span>
        </div>
      </div>
    </MetricWidget>
  );
};

// userId/accountId let an internal viewer (CRM Portal View) load a specific
// client's finance analytics instead of their own.
const FinanceAnalyticsTab = ({ userId = null, accountId = null }) => {
  const { darkMode } = useTheme();
  const { devMode } = useDevMode();
  const [timeframe, setTimeframe] = useState('ytd');

  const {
    loading,
    refresh,
    jobtreadConnected,
    jobtreadError,
    asOfDate,
    metrics,
    debug
  } = useFinanceAnalytics(timeframe, { userId });

  const m = metrics;
  const gpmStroke = statusStyle(m.gpm.status).stroke;
  const marginSeries = m.gpm.series || [];
  const hasMarginSeries = marginSeries.some((p) => p.gpm != null);
  const showConnectHero = !loading && !jobtreadConnected;

  // Combined A/R + A/P meter — bars scaled to the larger side so the two are
  // visually comparable. Missing side reads as $0 rather than "—" in context.
  const arVal = m.receivables.rawValue ?? 0;
  const apVal = m.payables.rawValue ?? 0;
  const arApMax = Math.max(arVal, apVal, 1);
  const arApEmpty = m.receivables.source === 'none' && m.payables.source === 'none';

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
        <div>
          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            Your live profitability picture, as of{' '}
            {asOfDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ConnectionPill connected={jobtreadConnected} label="JobTread" />
          {!showConnectHero && (
          <select
            value={timeframe}
            onChange={(e) => setTimeframe(e.target.value)}
            title="Timeframe"
            className={`px-3 py-1.5 rounded-lg border text-sm font-medium focus:outline-none focus:ring-2 focus:ring-red-500 ${
              darkMode
                ? 'bg-gray-800 border-gray-700 text-gray-200'
                : 'bg-white border-gray-200 text-gray-700'
            }`}
          >
            {FINANCE_TIMEFRAMES.map((t) => (
              <option key={t.id} value={t.id}>
                {t.label}
              </option>
            ))}
          </select>
          )}
        </div>
      </div>

      {devMode && <DevModePanel debug={debug} page="finance" />}

      {!loading && jobtreadConnected && jobtreadError && (
        <div className="flex items-start gap-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-700 text-amber-800 dark:text-amber-200 px-4 py-3 rounded-lg mb-6 text-sm">
          <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">JobTread data couldn&apos;t be loaded</p>
            <p className="mt-0.5">{jobtreadError}</p>
          </div>
        </div>
      )}

      {showConnectHero ? (
        <ConnectJobTreadHero
          description={JT_HERO_DESCRIPTION}
          clientUserId={userId}
          onConnected={refresh}
        />
      ) : (
      <>
      {/* Left 3/4: tall margins chart · Right 1/4: Gross Profit / Net Margin / A-R */}
      <div className="grid grid-cols-1 xl:grid-cols-4 gap-5 mb-6">
        <div className="xl:col-span-3 flex flex-col [&>div]:flex-1">
          <MetricWidget
            stretchChart
            title="Gross Profit Margin"
            icon={<Percent className="w-4 h-4" />}
            value={m.gpm.value}
            valueStatus={m.gpm.status}
            showStatusPill={false}
            source={m.gpm.source}
            loading={loading}
            empty={m.gpm.source === 'none'}
            emptyText="No signed orders with costs in JobTread for this timeframe yet."
            trace={devMode ? m.gpm.trace : null}
          >
            {hasMarginSeries && (
              <>
                <div className="flex-1 min-h-[16rem]">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={marginSeries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="gpmFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={gpmStroke} stopOpacity={0.25} />
                          <stop offset="95%" stopColor={gpmStroke} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={darkMode ? '#374151' : '#e5e7eb'} />
                      <XAxis
                        dataKey="label"
                        tick={{ fontSize: 11, fill: darkMode ? '#9ca3af' : '#6b7280' }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tickFormatter={fmtPctAxis}
                        tick={{ fontSize: 11, fill: darkMode ? '#9ca3af' : '#6b7280' }}
                        axisLine={false}
                        tickLine={false}
                        width={44}
                      />
                      <Tooltip
                        formatter={(v, name, props) => {
                          if (v == null) return ['—', 'Gross margin'];
                          const rev = props?.payload?.revenue;
                          const gp = props?.payload?.grossProfit;
                          const extra =
                            gp != null && rev != null
                              ? ` ($${Math.round(gp).toLocaleString()} on $${Math.round(rev).toLocaleString()})`
                              : '';
                          return [`${Number(v).toFixed(1)}%${extra}`, 'Gross margin'];
                        }}
                        contentStyle={
                          darkMode
                            ? { backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: 8, color: '#f9fafb' }
                            : { borderRadius: 8 }
                        }
                      />
                      <ReferenceLine y={0} stroke={darkMode ? '#4b5563' : '#d1d5db'} strokeWidth={1} />
                      <Area
                        type="monotone"
                        dataKey="gpm"
                        name="Gross margin"
                        stroke={gpmStroke}
                        strokeWidth={2}
                        fill="url(#gpmFill)"
                        connectNulls
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex flex-wrap items-center gap-x-5 gap-y-1.5 mt-2 text-sm text-gray-600 dark:text-gray-300">
                  <span className="flex items-center gap-2 font-medium">
                    <span className="w-3 h-3 rounded-full shrink-0" style={{ backgroundColor: gpmStroke }} />
                    Gross profit margin
                  </span>
                </div>
              </>
            )}
          </MetricWidget>
        </div>

        {/* Right column: Gross Profit ($), Net Margin (%), Net Profit ($) */}
        <div className="flex flex-col gap-5 [&>div]:flex-1">
          <MetricWidget
            title="Gross Profit"
            icon={<DollarSign className="w-4 h-4" />}
            value={m.grossProfit.value}
            valueStatus={m.grossProfit.status}
            source={m.grossProfit.source}
            sub={m.grossProfit.sub}
            loading={loading}
            empty={m.grossProfit.source === 'none'}
            emptyText="No signed orders with costs for this timeframe."
            trace={devMode ? m.grossProfit.trace : null}
          />
          <MetricWidget
            title="Net Margin"
            icon={<TrendingUp className="w-4 h-4" />}
            value={m.netMargin.value}
            valueStatus={m.netMargin.status}
            source={m.netMargin.source}
            sub={m.netMargin.sub}
            loading={loading}
            empty={m.netMargin.source === 'none'}
            emptyText={m.netMargin.cue || 'Net margin appears once overhead and costed orders exist.'}
            fullPagePath={m.netMargin.source === 'none' ? OVERHEAD_CALCULATOR_PATH : undefined}
            emptyActionLabel="Open Overhead Calculator"
            trace={devMode ? m.netMargin.trace : null}
          />
          <MetricWidget
            title="Net Profit"
            icon={<Wallet className="w-4 h-4" />}
            value={m.netProfit.value}
            valueStatus={m.netProfit.status}
            source={m.netProfit.source}
            sub={m.netProfit.sub}
            cue={m.netProfit.cue}
            loading={loading}
            empty={m.netProfit.source === 'none'}
            emptyText={m.netProfit.cue || 'Net profit appears once overhead and costed orders exist.'}
            fullPagePath={m.netProfit.source === 'none' ? OVERHEAD_CALCULATOR_PATH : undefined}
            emptyActionLabel="Open Overhead Calculator"
            trace={devMode ? m.netProfit.trace : null}
          />
        </div>
      </div>

      {/* Bottom row: Receivables & Payables (combined meter) · Overhead */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        <MetricWidget
          title="Receivables & Payables"
          icon={<ArrowLeftRight className="w-4 h-4" />}
          value={null}
          source={arApEmpty ? 'none' : 'jobtread'}
          loading={loading}
          empty={arApEmpty}
          emptyText="No open invoices or vendor bills in JobTread right now."
          trace={devMode ? m.payables.trace : null}
        >
          <div className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <ArrowDownToLine className="w-3.5 h-3.5 text-emerald-600 dark:text-emerald-400" />
                  Receivable
                </span>
                <span className="text-base font-bold text-gray-900 dark:text-white">{fmtDollar(arVal)}</span>
              </div>
              <div className="h-2.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                <div className="h-full rounded-full bg-emerald-500" style={{ width: `${(arVal / arApMax) * 100}%` }} />
              </div>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700 dark:text-gray-300">
                  <ArrowUpFromLine className="w-3.5 h-3.5 text-rose-600 dark:text-rose-400" />
                  Payable
                </span>
                <span className="text-base font-bold text-gray-900 dark:text-white">{fmtDollar(apVal)}</span>
              </div>
              <div className="h-2.5 rounded-full bg-gray-100 dark:bg-gray-700 overflow-hidden">
                <div className="h-full rounded-full bg-rose-500" style={{ width: `${(apVal / arApMax) * 100}%` }} />
              </div>
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-gray-100 dark:border-gray-700">
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">Net position</span>
              <span className={`text-sm font-semibold ${arVal - apVal < 0 ? 'text-rose-600 dark:text-rose-400' : 'text-emerald-600 dark:text-emerald-400'}`}>
                {fmtDollar(arVal - apVal)}
              </span>
            </div>
          </div>
        </MetricWidget>
        <OverheadQuickCard overhead={m.overhead} loading={loading} devMode={devMode} onSaved={refresh} saveUserId={userId} />
      </div>
      </>
      )}
    </div>
  );
};

export default FinanceAnalyticsTab;
